"use client";

import API, { type License } from "@/api/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useGenerateLicense } from "@/hooks/use-licenses";
import { type Org, useLicenseOrgs, useOrgUsersForLicense } from "@/hooks/use-orgs";
import { toast } from "@/lib/instrumented-toast";
import { formatDate, getDaysRemaining } from "@/lib/utils";
import { useOrgStore } from "@/stores";
import { SpanStatusCode, trace } from "@opentelemetry/api";
import { Check, ChevronDown, ChevronUp, Copy, Eye, EyeOff } from "lucide-react";
import { useMemo, useState } from "react";

const tracer = trace.getTracer("customer-portal-ui");

interface LicenseGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Organization currently being viewed; preselected until the user picks another. */
  defaultOrgId?: string;
}

export function LicenseGenerateDialog({ open, onOpenChange, defaultOrgId }: LicenseGenerateDialogProps) {
  const api = new API();
  const generateLicenseMutation = useGenerateLicense();
  const setOrg = useOrgStore.use.setOrg();

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLicense, setGeneratedLicense] = useState<License | null>(null);
  const [licenseKeyVisible, setLicenseKeyVisible] = useState(true);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedHolder, setCopiedHolder] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [useCustomWorkflow, setUseCustomWorkflow] = useState(false);

  // null = no explicit choice yet, so the selection falls back to defaultOrgId
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [customOrgName, setCustomOrgName] = useState("");
  const [customSelectedOrgId, setCustomSelectedOrgId] = useState<string | null>(null);

  const [selectedUserEmail, setSelectedUserEmail] = useState("");

  const [showInviteConfirm, setShowInviteConfirm] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);

  const licenseOrgsQuery = useLicenseOrgs();

  const availableOrgs = useMemo(() => {
    if (!licenseOrgsQuery.data) return [];
    return [...licenseOrgsQuery.data].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [licenseOrgsQuery.data]);

  const selectedOrg: Org | null = availableOrgs.find((o) => o.id === (selectedOrgId ?? defaultOrgId)) ?? null;
  const orgUsersQuery = useOrgUsersForLicense(selectedOrg?.id, !useCustomWorkflow && !!selectedOrg);

  const availableUsers = useMemo(() => {
    if (!orgUsersQuery.data) return [];
    return [...orgUsersQuery.data].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [orgUsersQuery.data]);

  const userComboboxOptions: ComboboxOption[] = useMemo(() => {
    return availableUsers.map((user) => ({
      value: user.email,
      label: user.name,
      subtitle: user.email,
    }));
  }, [availableUsers]);

  const orgComboboxOptions: ComboboxOption[] = useMemo(() => {
    return availableOrgs.map((org) => ({
      value: org.name,
      label: org.name,
      subtitle: `ID: ${org.id}`,
    }));
  }, [availableOrgs]);

  const handleCustomOrgNameChange = (value: string) => {
    setCustomOrgName(value);
    const match = availableOrgs.find((o) => o.name === value);
    setCustomSelectedOrgId(match?.id ?? null);
  };

  const maxExpirationDate = new Date();
  maxExpirationDate.setDate(maxExpirationDate.getDate() + 396);

  const [expirationDate, setExpirationDate] = useState(maxExpirationDate);
  const [selectedPreset, setSelectedPreset] = useState("max");
  const [notes, setNotes] = useState("");
  const [notesExpanded, setNotesExpanded] = useState(false);

  // Quick selection presets
  const setExpirationPreset = (months: number, presetKey: string) => {
    const date = new Date();
    if (months === -1) {
      // Max (396 days)
      date.setDate(date.getDate() + 396);
    } else {
      // Calculate approximate days for months
      date.setDate(date.getDate() + months * 30);
    }

    // Ensure we don't exceed max
    if (date > maxExpirationDate) {
      setExpirationDate(maxExpirationDate);
    } else {
      setExpirationDate(date);
    }
    setSelectedPreset(presetKey);
  };

  // Calculate days from today to selected date
  const calculateDaysFromDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const diffTime = selectedDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.min(diffDays, 396)); // Between 1 and 396 days
  };

  const totalDays = calculateDaysFromDate(expirationDate);
  const isMaxDuration = totalDays >= 396;

  const handleGenerate = async () => {
    const span = tracer.startSpan("flow.license_generation", {
      attributes: {
        "organization.id": selectedOrg?.id || "",
        "organization.name": selectedOrg?.name || customOrgName,
        "license.type": useCustomWorkflow ? "custom_org" : "existing_org",
        "license.has_custom_expiration": totalDays !== 396,
        "license.expiration_days": totalDays,
      },
    });

    try {
      span.addEvent("validating_input");

      if (useCustomWorkflow) {
        if (!customOrgName.trim()) {
          span.setAttribute("validation.failed", true);
          span.setAttribute("validation.error", "missing_org_name");
          span.end();
          toast.error("Please enter an organization name");
          return;
        }

        if (!selectedUserEmail.trim()) {
          span.setAttribute("validation.failed", true);
          span.setAttribute("validation.error", "missing_email");
          span.end();
          toast.error("Please enter an email address");
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(selectedUserEmail)) {
          span.setAttribute("validation.failed", true);
          span.setAttribute("validation.error", "invalid_email");
          span.end();
          toast.error("Please enter a valid email address");
          return;
        }
      } else {
        if (!selectedOrg) {
          span.setAttribute("validation.failed", true);
          span.setAttribute("validation.error", "missing_org");
          span.end();
          toast.error("Please select an organization");
          return;
        }

        if (!selectedUserEmail || !selectedUserEmail.includes("@")) {
          span.setAttribute("validation.failed", true);
          span.setAttribute("validation.error", "invalid_email");
          span.end();
          toast.error("Please enter a valid email address");
          return;
        }

        const emailExistsInOrg = availableUsers.some((user) => user.email === selectedUserEmail);
        if (!emailExistsInOrg) {
          span.addEvent("user_not_found_showing_invitation_prompt");
          span.end();
          setPendingEmail(selectedUserEmail);
          setShowInviteConfirm(true);
          return;
        }
      }

      if (totalDays === 0) {
        span.setAttribute("validation.failed", true);
        span.setAttribute("validation.error", "missing_expiration");
        span.end();
        toast.error("Please set an expiration period");
        return;
      }

      setIsGenerating(true);
      try {
        let orgId: string;
        let orgName: string;
        let email: string;

        if (useCustomWorkflow) {
          orgId = customSelectedOrgId || "";
          orgName = customOrgName.trim();
          email = selectedUserEmail.trim();
        } else {
          if (!selectedOrg) {
            toast.error("Organization not selected");
            setIsGenerating(false);
            span.end();
            return;
          }
          orgId = selectedOrg.id;
          orgName = selectedOrg.name;
          email = selectedUserEmail;
        }

        span.addEvent("generating_license");
        const license = await generateLicenseMutation.mutateAsync({
          organization_id: orgId,
          organization_name: orgName,
          assigned_user_email: email,
          custom_expiration_days: totalDays,
          notes: notes || undefined,
        });

        span.setAttribute("license.id", license.id || "");
        span.setAttribute("license.expiration_date", license.expiration_date || "");
        span.addEvent("license_created");

        if (license.license_key) {
          span.addEvent("license_key_copied");
        }

        setGeneratedLicense(license);
        setLicenseKeyVisible(true);
        setIsGenerating(false);
        if (license.organization_id && license.organization_name) {
          setOrg({ id: license.organization_id, name: license.organization_name });
        }
        span.setStatus({ code: SpanStatusCode.OK });
      } catch (error) {
        console.error(error);
        setIsGenerating(false);
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error instanceof Error ? error.message : String(error),
        });
        span.addEvent("license_generation_failed", {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      span.end();
    }
  };

  const handleInviteAndContinue = async () => {
    if (!selectedOrg || !pendingEmail) {
      return;
    }

    setIsInviting(true);
    try {
      // Extract name from email or use generic name
      const emailPrefix = pendingEmail.split("@")[0];
      const firstName = emailPrefix || "New";
      const lastName = "User";

      // Call the API to add user
      await api.addUser(selectedOrg.id, {
        profile: {
          first_name: firstName,
          last_name: lastName,
          display_name: pendingEmail,
        },
        email: {
          email: pendingEmail,
        },
        user_name: pendingEmail,
        role_keys: ["ORGVIEWER"],
      });

      toast.success(`Invitation sent to ${pendingEmail}`);
      setShowInviteConfirm(false);

      setIsGenerating(true);
      try {
        const license = await generateLicenseMutation.mutateAsync({
          organization_id: selectedOrg.id,
          organization_name: selectedOrg.name,
          assigned_user_email: pendingEmail,
          custom_expiration_days: totalDays,
          notes: notes || undefined,
        });

        setGeneratedLicense(license);
        setLicenseKeyVisible(true);
        setIsGenerating(false);
        if (license.organization_id && license.organization_name) {
          setOrg({ id: license.organization_id, name: license.organization_name });
        }
      } catch (error) {
        console.error(error);
        setIsGenerating(false);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      const isAlreadyRegistered =
        msg.includes("already registered") ||
        msg.includes("No invitation was sent") ||
        msg.includes("already a member");
      if (isAlreadyRegistered) {
        const title = msg.includes("already a member") ? "Already in this organization" : "User already exists";
        toast.error(title, {
          description: msg,
          duration: 10000,
        });
      } else {
        toast.error(`Failed to send invitation: ${msg}`);
      }
      console.error(error);
    } finally {
      setIsInviting(false);
    }
  };

  const handleCopyKey = () => {
    if (generatedLicense?.license_key) {
      navigator.clipboard.writeText(generatedLicense.license_key);
      setCopiedKey(true);
      toast.success("License key copied to clipboard");
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleCopyHolder = () => {
    if (generatedLicense?.license_holder) {
      navigator.clipboard.writeText(generatedLicense.license_holder);
      setCopiedHolder(true);
      setTimeout(() => setCopiedHolder(false), 2000);
    }
  };

  const handleCopyField = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getStatusBadge = (license: License) => {
    if (!license.is_active) return <Badge variant="destructive">Inactive</Badge>;
    const daysRemaining = getDaysRemaining(license.expiration_date);
    if (daysRemaining < 0) return <Badge variant="destructive">Expired</Badge>;
    if (daysRemaining < 30) return <Badge variant="secondary">Expiring Soon</Badge>;
    return <Badge variant="default">Active</Badge>;
  };

  const handleClose = () => {
    setGeneratedLicense(null);
    setLicenseKeyVisible(true);
    setCopiedKey(false);
    setCopiedHolder(false);
    setCopiedField(null);
    setUseCustomWorkflow(false);
    setSelectedOrgId(null);
    setCustomOrgName("");
    setCustomSelectedOrgId(null);
    setSelectedUserEmail("");
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 396);
    setExpirationDate(maxDate);
    setSelectedPreset("max");
    setNotes("");
    setNotesExpanded(false);
    setShowInviteConfirm(false);
    setPendingEmail("");
    setIsInviting(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] flex-col p-0 sm:max-w-[600px]">
        <DialogHeader className="sticky top-0 z-10 border-b bg-background px-6 py-4">
          <DialogTitle>Generate License</DialogTitle>
          <DialogDescription>Create a new license for an organization</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto px-6 pb-6">
          {generatedLicense ? (
            <div className="space-y-4 py-4">
              {/* License Holder */}
              <div className="rounded border-l-4 border-blue-500 bg-blue-50 py-3 pl-4 dark:bg-blue-950/20">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-muted-foreground">License Holder</h3>
                    <p className="mt-1 break-all font-mono text-sm">{generatedLicense.license_holder}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Required for license validation - cryptographically encoded in key
                    </p>
                  </div>
                  <Button variant="outline" size="icon" onClick={handleCopyHolder} className="shrink-0">
                    {copiedHolder ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* License Key */}
              <div className="rounded border-l-4 border-green-500 bg-green-50 py-3 pl-4 dark:bg-green-950/20">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-muted-foreground">License Key</h3>
                      {licenseKeyVisible && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setLicenseKeyVisible(false)}
                          className="h-6 gap-1 px-2 py-0 text-xs"
                        >
                          <EyeOff className="h-3 w-3" />
                          Click to hide
                        </Button>
                      )}
                    </div>
                    {licenseKeyVisible ? (
                      <p className="mt-1 break-all font-mono text-sm">{generatedLicense.license_key}</p>
                    ) : (
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setLicenseKeyVisible(true)}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Click to reveal license key
                        </Button>
                      </div>
                    )}
                  </div>
                  {licenseKeyVisible && (
                    <Button variant="outline" size="icon" onClick={handleCopyKey} className="shrink-0">
                      {copiedKey ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>

              {/* Important notice */}
              <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-950">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  <strong>Important:</strong> Save this license key securely. The key is cryptographically generated and
                  cannot be recovered.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">Organization</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleCopyField(generatedLicense.organization_name, "org")}
                    >
                      {copiedField === "org" ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1">{generatedLicense.organization_name}</p>
                  {generatedLicense.organization_id && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Organization ID: <code className="text-xs">{generatedLicense.organization_id}</code>
                    </p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                  <div className="mt-1">{getStatusBadge(generatedLicense)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">Created Date</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleCopyField(formatDate(generatedLicense.created_at), "created")}
                    >
                      {copiedField === "created" ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1">{formatDate(generatedLicense.created_at)}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground">Expiration Date</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleCopyField(formatDate(generatedLicense.expiration_date), "expiration")}
                    >
                      {copiedField === "expiration" ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                  <p className="mt-1">{formatDate(generatedLicense.expiration_date)}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-muted-foreground">Created By</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleCopyField(generatedLicense.created_by_user_email, "createdBy")}
                  >
                    {copiedField === "createdBy" ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
                <p className="mt-1">{generatedLicense.created_by_user_email}</p>
              </div>

              {generatedLicense.notes && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Notes</h3>
                  <p className="mt-1">{generatedLicense.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Workflow Mode Selection */}
              <div className="space-y-2">
                <Label>License Generation Mode</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant={!useCustomWorkflow ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUseCustomWorkflow(false)}
                    className="flex-1"
                  >
                    Standard Workflow
                  </Button>
                  <Button
                    type="button"
                    variant={useCustomWorkflow ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUseCustomWorkflow(true)}
                    className="flex-1"
                  >
                    Custom Workflow
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {useCustomWorkflow
                    ? "Select an existing organization or enter a new name, with any email address."
                    : "Select from existing organizations and users in the portal."}
                </p>
              </div>

              <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-950">
                <p className="text-sm text-yellow-900 dark:text-yellow-100">
                  <strong>License Key Format:</strong> The generated key cryptographically encodes the organization
                  name, assigned user email, and expiration date using SHA-256 hashing. Once generated, these values
                  cannot be changed.
                </p>
              </div>

              {/* Conditional Organization Selection */}
              {useCustomWorkflow ? (
                // Custom: combobox showing existing orgs + free-text for new ones
                <div className="space-y-2">
                  <Label htmlFor="custom-org-name">
                    Organization Name *<span className="ml-1 text-xs text-muted-foreground">(encoded in key)</span>
                  </Label>
                  <Combobox
                    options={orgComboboxOptions}
                    value={customOrgName}
                    onValueChange={handleCustomOrgNameChange}
                    placeholder="Select an existing org or type a new name..."
                    searchPlaceholder="Search organizations or type new name..."
                    emptyText="No matching organizations — press Enter to use typed name"
                    allowCustomValue={true}
                  />
                  <p className="text-xs text-muted-foreground">
                    {customSelectedOrgId
                      ? `Using existing organization (ID: ${customSelectedOrgId})`
                      : "New organization name — will be encoded in the license key"}
                  </p>
                </div>
              ) : (
                // Standard Organization Selection
                <div className="space-y-2">
                  <Label htmlFor="org-select">
                    Organization *<span className="ml-1 text-xs text-muted-foreground">(encoded in key)</span>
                  </Label>
                  <Select value={selectedOrg?.id} onValueChange={setSelectedOrgId}>
                    <SelectTrigger id="org-select">
                      <SelectValue placeholder="Select an organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableOrgs.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedOrg && <p className="text-xs text-muted-foreground">ID: {selectedOrg.id}</p>}
                </div>
              )}

              {/* Conditional User Email Selection */}
              {useCustomWorkflow ? (
                // Custom Email Input
                <div className="space-y-2">
                  <Label htmlFor="custom-email-input">
                    Assigned User Email *
                    <span className="ml-1 text-xs text-muted-foreground">(encoded in key, cannot be changed)</span>
                  </Label>
                  <Input
                    id="custom-email-input"
                    type="email"
                    value={selectedUserEmail}
                    onChange={(e) => setSelectedUserEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the email address for the license holder. This email will be cryptographically encoded in the
                    license key.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>
                    Assigned User Email *
                    <span className="ml-1 text-xs text-muted-foreground">(encoded in key, cannot be changed)</span>
                  </Label>

                  <Combobox
                    options={userComboboxOptions}
                    value={selectedUserEmail}
                    onValueChange={setSelectedUserEmail}
                    disabled={!selectedOrg}
                    placeholder={
                      !selectedOrg
                        ? "Select an organization first"
                        : orgUsersQuery.isLoading
                          ? "Loading users..."
                          : "Search for a user or enter custom email..."
                    }
                    searchPlaceholder="Type to search users or enter email..."
                    emptyText="No users found matching your search"
                    allowCustomValue={true}
                  />
                  <p className="text-xs text-muted-foreground">
                    Select an existing user from the organization, or enter a custom email. If the email is not found,
                    you'll be prompted to invite them.
                  </p>
                </div>
              )}

              {/* Expiration Date Picker */}
              <div className="space-y-2">
                <Label>
                  Expiration Date *
                  <span className="ml-1 text-xs text-muted-foreground">(encoded in key, max 396 days)</span>
                </Label>

                {/* Quick Selection Dropdown */}
                <div className="mb-3">
                  <Label htmlFor="quick-select" className="text-xs text-muted-foreground">
                    Quick Selection
                  </Label>
                  <Select
                    value={selectedPreset}
                    onValueChange={(value) => {
                      const presets: Record<string, number> = {
                        "1month": 1,
                        "3months": 3,
                        "6months": 6,
                        "1year": 12,
                        max: -1,
                      };
                      setExpirationPreset(presets[value], value);
                    }}
                  >
                    <SelectTrigger id="quick-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1month">1 Month</SelectItem>
                      <SelectItem value="3months">3 Months</SelectItem>
                      <SelectItem value="6months">6 Months</SelectItem>
                      <SelectItem value="1year">1 Year</SelectItem>
                      <SelectItem value="max">Max (396 days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Picker */}
                <div>
                  <Label htmlFor="expiration-date" className="text-xs text-muted-foreground">
                    Or select a specific date
                  </Label>
                  <div className="relative">
                    <Input
                      id="expiration-date"
                      type="date"
                      value={expirationDate.toISOString().split("T")[0]}
                      onChange={(e) => {
                        const newDate = new Date(e.target.value);
                        if (newDate <= maxExpirationDate) {
                          setExpirationDate(newDate);
                          setSelectedPreset("custom"); // Set to custom when manually changed
                        } else {
                          setExpirationDate(maxExpirationDate);
                          toast.info("Maximum expiration is 396 days");
                        }
                      }}
                      min={new Date().toISOString().split("T")[0]}
                      max={maxExpirationDate.toISOString().split("T")[0]}
                      className="w-full [color-scheme:light] dark:[color-scheme:dark]"
                    />
                  </div>
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm font-medium">Duration:</span>
                  <Badge variant={isMaxDuration ? "default" : "secondary"}>
                    {totalDays} days (~{Math.round(totalDays / 30)} months)
                    {isMaxDuration && " - MAX"}
                  </Badge>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help text-xs text-muted-foreground">
                        until {formatDate(expirationDate)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {expirationDate.toLocaleDateString(navigator.language, {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              {/* Notes - Collapsible */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    className="h-6 text-xs"
                  >
                    {notesExpanded ? (
                      <>
                        <ChevronUp className="mr-1 h-3 w-3" />
                        Collapse
                      </>
                    ) : (
                      <>
                        <ChevronDown className="mr-1 h-3 w-3" />
                        Expand
                      </>
                    )}
                  </Button>
                </div>
                {notesExpanded && (
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes (e.g., customer contact, use case, billing info)..."
                    rows={3}
                  />
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            {generatedLicense ? (
              <Button onClick={handleClose}>Done</Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={handleClose} disabled={isGenerating}>
                  Cancel
                </Button>
                <Button onClick={handleGenerate} disabled={isGenerating || (!useCustomWorkflow && !selectedOrg)}>
                  {isGenerating ? "Generating..." : "Generate License"}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
      </DialogContent>

      {/* Invitation Confirmation Dialog */}
      <Dialog open={showInviteConfirm} onOpenChange={setShowInviteConfirm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Invite User to Portal?</DialogTitle>
            <DialogDescription>
              The email <strong>{pendingEmail}</strong> is not registered in <strong>{selectedOrg?.name}</strong>. Would
              you like to invite them as a VIEWER before generating the license?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowInviteConfirm(false)} disabled={isInviting}>
              Cancel
            </Button>
            <Button onClick={handleInviteAndContinue} disabled={isInviting} variant="default">
              {isInviting ? "Inviting..." : "Invite & Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
